import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { widgetsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  EyeIcon,
  ComputerDesktopIcon,
  ClipboardIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

export default function AdminWidgets() {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: widgetConfig, isLoading } = useQuery('widgetConfig', widgetsAPI.getWidgetConfig);

  const updateMutation = useMutation(
    (config) => widgetsAPI.updateWidgetConfig(config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('widgetConfig');
        toast.success('Website Chat settings saved!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to save settings');
      },
    }
  );

  const [formData, setFormData] = useState({
    widget_title: '',
    widget_subtitle: '',
    welcome_message: '',
    primary_color: '#007073',
    secondary_color: '#666666',
    is_enabled: true,
  });

  React.useEffect(() => {
    if (widgetConfig) {
      setFormData({
        widget_title: widgetConfig.title || '',
        widget_subtitle: widgetConfig.subtitle || '',
        welcome_message: widgetConfig.welcome_message || '',
        primary_color: widgetConfig.primary_color || '#007073',
        secondary_color: widgetConfig.secondary_color || '#666666',
        is_enabled: widgetConfig.is_enabled ?? true,
      });
    }
  }, [widgetConfig]);

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Backend route is `/api/widgets/{tenant_id}/embed.js` (path param). The
  // generated script hardcodes the tenant from the URL — `data-tenant-id`
  // was never read, so it's dropped.
  const embedCode = `<script src="${process.env.REACT_APP_API_URL || 'https://cleona.ragamuffin.app'}/api/widgets/${user?.tenantId || 'YOUR_TENANT_ID'}/embed.js"></script>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Embed code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const previewWidget = () => {
    // Backend route is `/api/widgets/{tenant_id}/preview` (path param),
    // not `/api/widgets/preview?tenant_id=…` — the query-string form 404s.
    window.open(`${process.env.REACT_APP_API_URL || ''}/api/widgets/${user?.tenantId}/preview`, '_blank');
  };

  const testInSampleSite = () => {
    // Open a simple test page in a new window
    const testHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Website Chat Test Page</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; background: #f5f5f5; }
            h1 { color: #333; }
            p { color: #666; max-width: 600px; }
          </style>
        </head>
        <body>
          <h1>Website Chat Test Page</h1>
          <p>This is a sample page to test your chat widget. The widget should appear in the bottom-right corner of this page.</p>
          <p>Try asking questions about your uploaded documents to see how the assistant responds.</p>
          <script src="${process.env.REACT_APP_API_URL || ''}/api/widgets/${user?.tenantId}/embed.js"></script>
        </body>
      </html>
    `;
    const blob = new Blob([testHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cleona-600"></div>
      </div>
    );
  }

  return (
    <div className="font-source">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-arsenal">Add the SmartAssistant&trade; Chat to Your Website</h1>
        <p className="mt-1 text-gray-600">
          Place your CLEONA SmartAssistant&trade; on your website so users can ask questions and get instant support without logging in.
        </p>
      </div>

      {/* Widget Settings Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Website Chat Settings</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Chat Title
              </label>
              <input
                type="text"
                name="widget_title"
                value={formData.widget_title}
                onChange={handleInputChange}
                placeholder="Your Company Assistant"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Chat Subtitle
              </label>
              <input
                type="text"
                name="widget_subtitle"
                value={formData.widget_subtitle}
                onChange={handleInputChange}
                placeholder="How can I help you?"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="flex items-center">
                <div
                  className="w-10 h-12 rounded-l-lg border border-r-0 border-gray-200"
                  style={{ backgroundColor: formData.primary_color }}
                />
                <input
                  type="text"
                  name="primary_color"
                  value={formData.primary_color}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Color
              </label>
              <div className="flex items-center">
                <div
                  className="w-10 h-12 rounded-l-lg border border-r-0 border-gray-200"
                  style={{ backgroundColor: formData.secondary_color }}
                />
                <input
                  type="text"
                  name="secondary_color"
                  value={formData.secondary_color}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Welcome Message
            </label>
            <textarea
              name="welcome_message"
              value={formData.welcome_message}
              onChange={handleInputChange}
              rows={4}
              placeholder="Hello! I'm the Your Company assistant. How can I help you today?"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-6 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Bottom Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Widget Status Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-6">
            Website Chat Status
          </h2>

          <div className="mb-6 pb-6 border-b border-gray-100">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Current Status</p>
            <div className="flex items-center justify-between">
              <span className={`font-semibold ${formData.is_enabled ? 'text-green-500' : 'text-red-500'}`}>
                {formData.is_enabled ? 'ACTIVE' : 'INACTIVE'}
              </span>
              <div className={`w-4 h-4 rounded-full ${formData.is_enabled ? 'bg-green-500' : 'bg-red-500'}`} />
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={previewWidget}
              className="flex items-center gap-3 text-cleona-600 hover:text-cleona-700 font-medium"
            >
              <EyeIcon className="h-5 w-5" />
              <span>PREVIEW WEBSITE CHAT <span className="font-normal">(NEW TAB)</span></span>
            </button>

            <button
              onClick={testInSampleSite}
              className="flex items-center gap-3 text-cleona-600 hover:text-cleona-700 font-medium"
            >
              <ComputerDesktopIcon className="h-5 w-5" />
              <span>TEST WEBSITE CHAT IN SAMPLE SITE</span>
            </button>
          </div>
        </div>

        {/* Embed Code Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-4">
            Embed Code
          </h2>

          <p className="text-sm text-gray-500 uppercase tracking-wide mb-4">
            Copy this code and paste it into your website's HTML where you want the Website Chat to appear.
          </p>

          <div className="relative">
            <div className="bg-gray-100 rounded-lg p-4 pr-12">
              <code className="text-sm text-gray-800 break-all font-mono">
                {embedCode}
              </code>
            </div>
            <button
              onClick={copyEmbedCode}
              className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 bg-white rounded-lg shadow-sm border border-gray-200 transition-colors"
            >
              {copied ? (
                <CheckIcon className="h-5 w-5 text-green-500" />
              ) : (
                <ClipboardIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
